import { isValidConvexId } from "@plasma/utils/convex-id";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, ShieldAlert } from "lucide-react";

import { EmployeeProfileBlocks } from "~/components/employees/employee-profile-blocks";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { toConvexTableId } from "~/lib/convex-table-id";

export const Route = createFileRoute("/_authenticated/employees/$id")({
  component: EmployeePage,
});

function EmployeePage() {
  const { id } = Route.useParams();
  const { hasPermission, isLoading } = useEmployeePermissions();
  const canEditEmployee = hasPermission("editEmployee");

  // Validate ID format before making any queries
  if (!isValidConvexId(id)) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Employee Not Found</AlertTitle>
        <AlertDescription className="flex flex-col gap-3">
          <span>
            The employee ID in the URL is invalid or the employee doesn't exist.
          </span>
          <Button asChild className="w-fit" size="sm" variant="outline">
            <Link to="/employees">Back to Employees</Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Loading state
  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  // No permission to edit employees
  if (!canEditEmployee) {
    return (
      <Alert variant="destructive">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to edit employee profiles. Contact your
          manager to request access.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <EmployeeProfileBlocks employeeId={toConvexTableId("employees", id)} />
  );
}
